import CalendarIcon from '@/components/Icon/CalendarIcon';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import { useParsedUseDate } from '@/hooks/useDate';
import useSeason, {
	getDaysUntilNextSeason,
	getDutchSeasonName,
	getNextSeason,
} from '@/hooks/useSeason';
import { setSeasonPalette } from '@/resources/seasons';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef } from 'react';

const SeasonCountdown = () => {
	const selfRef = useRef<HTMLDivElement>(null);
	const reduceMotion = useReducedMotion();

	// Get next season information
	const season = useSeason();
	const date = useParsedUseDate();
	const nextSeason = useMemo(() => getNextSeason(season), [season]);
	const nextSeasonDisplayName = useMemo(
		() => getDutchSeasonName(nextSeason),
		[nextSeason],
	);
	const daysUntilNextSeason = useMemo(
		() => getDaysUntilNextSeason(season, date),
		[season, date],
	);

	// Set this component to use the palette of the next season
	useEffect(() => {
		if (selfRef.current) setSeasonPalette(nextSeason, selfRef.current);
	}, [selfRef, nextSeason]);

	return (
		<div ref={selfRef} className="px-2 py-6 xs:px-6">
			<aside
				aria-label="Volgend seizoen"
				className="relative mx-auto w-full max-w-lg">
				<LargeSquareBackground className="text-white opacity-20" />
				<div className="relative grid grid-cols-2 items-center">
					<motion.img
						alt=""
						src={`/assets/illustrations/top_start_${nextSeason}.svg`}
						className="aspect-square w-full max-w-xs rounded-bl-[20%] rounded-tl-[40%]"
						whileInView={{
							rotate: reduceMotion ? 0 : [0, 6, 0],
							transition: {
								ease: 'backOut',
								duration: reduceMotion ? 0 : 1,
								delay: 0.4,
							},
						}}
					/>
					<motion.div
						className="relative me-auto p-6 text-white"
						initial={{
							opacity: 0,
						}}
						whileInView={{
							scale: 1.2,
							opacity: 1,
							rotate: 6,
							transition: {
								ease: 'backOut',
								duration: reduceMotion ? 0 : 0.5,
								delay: 0.2,
							},
						}}>
						<p aria-hidden className="grid text-center">
							<span className="relative mx-auto text-accentOrCompatLightest">
								<LargeSquareBackground className="translate-x-1 scale-125 text-accentOrCompatDarkest" />
								<span className="relative block">
									<CalendarIcon className="h-24 lg:h-24" />
									<span className="absolute inset-x-0 top-7 w-full ps-2 text-center font-brand text-5xl">
										{daysUntilNextSeason}
									</span>
								</span>
							</span>
						</p>
					</motion.div>
					<p className="relative col-span-2 -mt-6 text-accentOrCompatDarkest xxs:ms-24">
						<LabelBackground className="-scale-y-100 text-accentOrCompatLightest" />
						<span className="relative block px-6 py-4 text-lg">
							De {nextSeasonDisplayName} begint over{' '}
							{daysUntilNextSeason}{' '}
							{daysUntilNextSeason === 1 ? 'dag' : 'dagen'}.
							Ontdek dan de {nextSeasonDisplayName}soorten!
						</span>
					</p>
				</div>
			</aside>
		</div>
	);
};

export default SeasonCountdown;
